/**
 * TemplateFilters - Filter bar for template list
 *
 * Features:
 * - Search input
 * - Category filter
 * - Visibility filter
 * - Active status filter
 * - Filter count badge
 */
import { Search, X, Filter, Mail, MessageSquare, FileText, ClipboardList } from 'lucide-react';
import {
  Input,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  cn,
} from '@respectly/ui';
import type { TemplateCategory, TemplateVisibility, TemplateListFilters } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface TemplateFiltersProps {
  filters: TemplateListFilters;
  onFilterChange: <K extends keyof TemplateListFilters>(key: K, value: TemplateListFilters[K]) => void;
  onClearFilters: () => void;
  activeFiltersCount?: number;
  className?: string;
}

// ============================================
// Configuration
// ============================================

const categoryOptions: { value: TemplateCategory; label: string; icon: typeof Mail }[] = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'document', label: 'Document', icon: FileText },
  { value: 'form', label: 'Form', icon: ClipboardList },
];

const visibilityOptions: { value: TemplateVisibility; label: string }[] = [
  { value: 'all', label: 'Public' },
  { value: 'plans', label: 'Plan-Specific' },
  { value: 'tenants', label: 'Account-Specific' },
  { value: 'none', label: 'Hidden' },
];

// ============================================
// Component
// ============================================

export function TemplateFilters({
  filters,
  onFilterChange,
  onClearFilters,
  activeFiltersCount = 0,
  className,
}: TemplateFiltersProps) {
  return (
    <div className={cn('flex flex-col sm:flex-row gap-3', className)}>
      {/* Search Input */}
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search templates..."
          value={filters.search || ''}
          onChange={(e) => onFilterChange('search', e.target.value || undefined)}
          className="pl-9 pr-9"
        />
        {filters.search && (
          <button
            onClick={() => onFilterChange('search', undefined)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Category Filter */}
      <Select
        value={filters.category || 'all-categories'}
        onValueChange={(val) => onFilterChange('category', val === 'all-categories' ? undefined : val as TemplateCategory)}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all-categories">All Categories</SelectItem>
          {categoryOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <div className="flex items-center gap-2">
                <option.icon className="h-4 w-4" />
                {option.label}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Visibility Filter */}
      <Select
        value={filters.visibility || 'all-visibility'}
        onValueChange={(val) => onFilterChange('visibility', val === 'all-visibility' ? undefined : val as TemplateVisibility)}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="Visibility" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all-visibility">All Visibility</SelectItem>
          {visibilityOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Status Filter */}
      <Select
        value={filters.isActive === undefined ? 'all-status' : filters.isActive ? 'active' : 'inactive'}
        onValueChange={(val) => {
          if (val === 'all-status') {
            onFilterChange('isActive', undefined);
          } else {
            onFilterChange('isActive', val === 'active');
          }
        }}
      >
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all-status">All Status</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>

      {/* Clear Filters */}
      {activeFiltersCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="h-10"
        >
          <Filter className="mr-1.5 h-4 w-4" />
          Clear
          <Badge variant="secondary" className="ml-1.5">
            {activeFiltersCount}
          </Badge>
        </Button>
      )}
    </div>
  );
}
